import { Link } from 'react-router-dom';
import { Alert, Badge, Card, EmptyState, Icon, Spinner, useAsync } from '@onepos/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { STATUS, daysLeft, fmtDate, fmtDaysLeft, fmtMoney, monthsText } from '../lib/format';
import type { BillingOverview, Dashboard } from '../lib/types';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Xayrli tun';
  if (h < 12) return 'Xayrli tong';
  if (h < 18) return 'Xayrli kun';
  return 'Xayrli kech';
}

export function DashboardPage() {
  const { state } = useAuth();
  const name = state.status === 'ready' ? state.me.full_name.split(' ')[0] : '';
  const { data, error, loading, reload } = useAsync(() => api.rpc<Dashboard>('platform_dashboard'), []);
  const bill = useAsync(() => api.rpc<BillingOverview>('platform_billing_overview'), []);
  const refresh = () => {
    reload();
    bill.reload();
  };

  const s = data?.by_status ?? {};
  const stats = [
    { label: 'Faol restoranlar', value: s.active ?? 0, hint: 'to‘lov qilingan' },
    { label: 'Sinov muddatida', value: s.trial ?? 0, hint: `${data?.trials_ending.length ?? 0} tasi 7 kunda tugaydi` },
    { label: 'Muhlat va to‘xtatilgan', value: (s.grace ?? 0) + (s.suspended ?? 0), hint: 'e’tibor kerak' },
    { label: 'Faol qurilmalar', value: data?.devices_active_24h ?? 0, hint: 'oxirgi 24 soatda' },
  ];

  return (
    <>
      <header className="cn-page-head">
        <div>
          <h1>
            {greeting()}
            {name ? `, ${name}` : ''}
          </h1>
          <p>Platformadagi restoranlar holati · {fmtDate(new Date().toISOString())}</p>
        </div>
        <button type="button" className="op-btn op-btn--secondary op-btn--sm" onClick={refresh} disabled={loading}>
          <Icon name="refresh" /> Yangilash
        </button>
      </header>

      {error && <Alert tone="danger">{error.message}</Alert>}

      <section className="cn-stats" aria-label="Ko‘rsatkichlar">
        {stats.map((st) => (
          <div key={st.label} className="op-card cn-stat">
            <span className="cn-stat__label">{st.label}</span>
            <span className="cn-stat__value op-num">{loading && !data ? '…' : st.value}</span>
            <span className="cn-stat__hint">{st.hint}</span>
          </div>
        ))}
      </section>

      {loading && !data ? (
        <Spinner />
      ) : data && data.total === 0 ? (
        <Card>
          <EmptyState
            title="Hali restoran yo‘q"
            action={
              <Link className="op-btn op-btn--primary" to="/restoranlar/yangi">
                <Icon name="plus" /> Birinchi restoranni ochish
              </Link>
            }
          >
            Restoran bir necha daqiqada ochiladi: menyu, sklad, retseptlar, zal va rollar tayyor holda.
          </EmptyState>
        </Card>
      ) : data ? (
        <div className="cn-two">
          <BillingCards b={bill.data} />
          <Card
            title="Yangi restoranlar"
            actions={
              <Link to="/restoranlar" className="op-btn op-btn--ghost op-btn--sm">
                Hammasi <Icon name="chevron" />
              </Link>
            }
          >
            <ul className="cn-list">
              {data.recent.map((t) => (
                <li key={t.id}>
                  <Link to={`/restoranlar/${t.id}`} className="cn-list__link">
                    <span className="cn-list__main">
                      {t.name}
                      <span className="cn-list__sub">
                        {t.city ?? '—'} · {fmtDate(t.created_at)}
                      </span>
                    </span>
                    <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}
    </>
  );
}

function BillingCards({ b }: { b: BillingOverview | null | undefined }) {
  if (!b) return null;
  const attention = b.overdue.length + b.suspended.length;
  return (
    <>
      <Card title="Obuna: daromad">
        <p className="cn-big op-num" style={{ margin: 0 }}>
          {fmtMoney(b.mrr)} <span className="cn-muted">/ oy</span>
        </p>
        <p className="cn-muted" style={{ marginTop: 4 }}>
          Faol obunalar (tarif + qo‘shimchalar){b.unpriced > 0 ? ` · ${b.unpriced} ta restoran tarifida narx yo‘q` : ''}
        </p>
        <ul className="cn-list cn-list--kv">
          <li>
            <span>Shu oy tushgan</span>
            <span className="op-num">
              {fmtMoney(b.month_income)} · {b.month_payments} ta to‘lov
            </span>
          </li>
          {b.recent_payments.slice(0, 4).map((p) => (
            <li key={p.id}>
              <Link to={`/restoranlar/${p.tenant_id}`}>{p.tenant_name}</Link>
              <span className="op-num cn-muted">
                {fmtMoney(p.amount)} · {monthsText(p.months)} · {fmtDate(p.created_at)}
              </span>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Muddati tugayotganlar" actions={<Badge tone={b.expiring.length ? 'info' : 'neutral'}>{b.expiring.length}</Badge>}>
        {b.expiring.length === 0 ? (
          <p className="cn-muted">7 kun ichida tugaydigan sinov yoki obuna yo‘q.</p>
        ) : (
          <ul className="cn-list">
            {b.expiring.map((t) => {
              const d = daysLeft(t.ends_at) ?? 0;
              return (
                <li key={t.id}>
                  <Link to={`/restoranlar/${t.id}`} className="cn-list__link">
                    <span className="cn-list__main">
                      {t.name}
                      <span className="cn-list__sub">{t.status === 'trial' ? 'sinov' : 'obuna'} · {fmtDate(t.ends_at)}</span>
                    </span>
                    <Badge tone={d <= 0 ? 'danger' : d <= 3 ? 'warning' : 'info'}>{fmtDaysLeft(t.ends_at)}</Badge>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      {attention > 0 && (
        <Card title="To‘lov kutilmoqda va to‘xtatilgan" actions={<Badge tone="warning">{attention}</Badge>}>
          <ul className="cn-list">
            {b.overdue.map((t) => (
              <li key={t.id}>
                <Link to={`/restoranlar/${t.id}`} className="cn-list__link">
                  <span className="cn-list__main">
                    {t.name}
                    <span className="cn-list__sub">
                      muddat {fmtDate(t.ends_at)} da tugagan
                      {t.suspend_at ? ` · ${fmtDate(t.suspend_at)} da to‘xtatiladi` : ' · avtomatik to‘xtatilmaydi'}
                    </span>
                  </span>
                  <Badge tone="warning">{STATUS.grace.label}</Badge>
                </Link>
              </li>
            ))}
            {b.suspended.map((t) => (
              <li key={t.id}>
                <Link to={`/restoranlar/${t.id}`} className="cn-list__link">
                  <span className="cn-list__main">
                    {t.name}
                    <span className="cn-list__sub">{fmtDate(t.since)} dan</span>
                  </span>
                  <Badge tone="danger">{STATUS.suspended.label}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
